import React, { useEffect, useState } from 'react';
import { DISCOVERY_DISMISS } from '../sandboxCopy';
import FullEhrFeaturePill from './FullEhrFeaturePill';

interface DiscoveryPopupProps {
  title: string;
  /** Newlines split into paragraphs (copy strings in sandboxCopy use \n). */
  body?: string;
  /** Optional extra line rendered like a CTA-styled hint (e.g. "Watch this example ▶"). */
  actionHint?: string;
  /** Rich content rendered after the body (e.g. the imported-charges receipt). */
  children?: React.ReactNode;
  onDismiss: () => void;
  dismissLabel?: string;
}

/**
 * A "Discovery control" pop-up (spec §UI Element Classes): visible + clickable
 * surfaces that don't recreate full EHR functionality open one of these small
 * dismissible cards instead. Renders as a centered card on desktop and a
 * bottom sheet on mobile, always with the FULL EHR FEATURE pill. The enter
 * transition is hand-rolled — the repo's animate-in utilities are no-ops.
 */
const DiscoveryPopup: React.FC<DiscoveryPopupProps> = ({
  title,
  body,
  actionHint,
  children,
  onDismiss,
  dismissLabel = DISCOVERY_DISMISS,
}) => {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const frame = requestAnimationFrame(() => setEntered(true));
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('keydown', onKey);
    };
  }, [onDismiss]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div
        className={`absolute inset-0 bg-gray-900/40 backdrop-blur-[2px] transition-opacity duration-200 ${
          entered ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onDismiss}
      />
      <div
        className={`relative w-full sm:w-auto sm:min-w-[26rem] sm:max-w-md overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 dark:bg-dark-gray-800 dark:ring-white/10 transition-all duration-200 ease-out ${
          entered ? 'translate-y-0 opacity-100 sm:scale-100' : 'translate-y-4 opacity-0 sm:scale-95'
        }`}
      >
        <div className="relative bg-gradient-to-br from-primary-50 via-white to-white px-6 pb-4 pt-5 dark:from-primary-900/25 dark:via-dark-gray-800 dark:to-dark-gray-800">
          <span
            aria-hidden
            className="pointer-events-none absolute -right-10 -top-12 h-32 w-32 rounded-full bg-primary-200/50 blur-2xl dark:bg-primary-700/20"
          />
          <FullEhrFeaturePill className="relative" />
          <h3 className="relative mt-3 text-lg font-bold leading-snug text-gray-900 dark:text-gray-100">
            {title}
          </h3>
        </div>

        <div className="space-y-2.5 px-6 pb-5">
          {(body ? body.split('\n') : []).map((paragraph, i) => (
            <p key={i} className="text-sm leading-relaxed text-gray-600 dark:text-gray-300">
              {paragraph}
            </p>
          ))}
          {actionHint ? (
            <p className="pt-1 text-sm font-semibold text-primary-600 dark:text-primary-400">
              {actionHint}
            </p>
          ) : null}
          {children}
        </div>

        <div className="flex justify-end border-t border-gray-100 bg-gray-50/80 px-6 py-3.5 dark:border-dark-gray-700 dark:bg-dark-gray-700/40">
          <button
            type="button"
            className="rounded-lg bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
            onClick={onDismiss}
          >
            {dismissLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiscoveryPopup;
